import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import DropdownCaret from '/public/icons/dropdown-caret.svg'
import Button from '../Button'
import { withModifiers } from '../../helpers/withModifiers'

const Dropdown = ({
  children,
  title,
  arrow = true,
  custom = false,
  disabled = false,
  active = false,
  handleClose = false,
  variant = [],
  buttonVariant = [],
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [width, setWidth] = useState(0)
  const [shouldRender, setShouldRender] = useState(false)
  const contentRef = useRef(null)
  const buttonRef = useRef(null)
  const wrapperRef = useRef(null)
  const timeoutRef = useRef(null)

  const isTouch =
    typeof window !== 'undefined' &&
    window?.matchMedia('(pointer: coarse)').matches

  const handleOpen = () => {
    setOpen(!open)
    setShouldRender(true)
    setTimeout(() => {
      !open && contentRef.current?.focus()
    }, 100)
  }

  const handleMouseEnter = () => {
    if (!isTouch && !disabled) {
      setOpen(true)
      setShouldRender(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isTouch) {
      setOpen(false)
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false)
      }, 200)
    }
  }

  // Keyboard accessibility
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (
        event.key === 'Enter' &&
        document.activeElement === buttonRef.current
      ) {
        event.preventDefault()
        setOpen(true)
        setShouldRender(true)
      }
    }

    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [])

  // Positioning logic
  const positionDropdown = () => {
    const centeredElement = wrapperRef.current
    const buttonElement = buttonRef.current

    if (!centeredElement) return

    const buttonRect = buttonElement.getBoundingClientRect()
    const centeredElementRect = centeredElement.getBoundingClientRect()
    const elementWidth = centeredElement.offsetWidth
    const viewportWidth = window.innerWidth

    if (
      (centeredElementRect.right === viewportWidth + 2 &&
        centeredElementRect.left !== 0) ||
      centeredElementRect.left < 0 ||
      centeredElementRect.right > viewportWidth
    ) {
      if (
        elementWidth + elementWidth / 2 > viewportWidth ||
        buttonRect.right + elementWidth / 2 > viewportWidth
      ) {
        if (buttonRect.left < elementWidth / 2) {
          centeredElement.style.left = `-${buttonRect.left - 2}px`
          centeredElement.style.transform = 'none'
          centeredElement.style.right = 'unset'
        }
        if (
          viewportWidth - buttonRect.right < elementWidth / 2 &&
          getComputedStyle(centeredElement).getPropertyValue('transform') !==
            'none'
        ) {
          centeredElement.style.right = `-${
            viewportWidth - buttonRect.right - 20
          }px`
          centeredElement.style.left = 'unset'
          centeredElement.style.transform = 'none'
        }
      }
    }
  }

  // Handle external close
  useEffect(() => {
    if (handleClose) {
      setOpen(false)
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false)
      }, 200)
    }
  }, [handleClose])

  // Positioning effect
  useEffect(() => {
    if (shouldRender) {
      positionDropdown()
    }
  }, [shouldRender])

  // Resize observer
  useEffect(() => {
    if (shouldRender && wrapperRef.current) {
      const observer = new ResizeObserver((entries) => {
        setWidth(entries[0].contentRect.width)
      })
      const currentRef = wrapperRef.current // Store reference
      observer.observe(currentRef)

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef)
        }
      }
    }
  }, [shouldRender])

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={withModifiers(
        'dropdown',
        [
          custom ? 'custom' : '',
          active && 'is-active',
          open && 'is-open',
          disabled && 'disabled',
          ...variant
        ],
        styles
      )}
    >
      <Button
        buttonRef={buttonRef}
        disabled={disabled}
        className={styles.dropdown__button}
        onClick={() => {
          if (isTouch || !open) {
            handleOpen()
          }
        }}
        variant={['label', ...buttonVariant]}
        arrow={false}
        {...props}
      >
        {title}
        {arrow && <DropdownCaret className={styles.dropdown__caret} />}
      </Button>

      {shouldRender && (
        <div
          width={width}
          ref={wrapperRef}
          className={`${styles.dropdown__wrapper} ${open ? styles.open : ''}`}
        >
          <div ref={contentRef} className={styles.dropdown__content}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dropdown
